import { AxiosResponse } from 'axios';
import getHttpClient from '../../common/http-common';

export interface DataGolTokenRequest {
    tenantCode: string;
    dashboardId: string;
}

export interface DataGolTokenResponse {
    userToken: string;
    expiresIn: number;
    userId: string;
    appId: string;
    dashboardId: string;
}

export interface EngageTile {
    name: string;
    description: string;
    appId: string;
    dashboardId: string;
}

export type LoadStatus = 'ok' | 'unauthorized' | 'failed';

export interface ErrorState {
    message: string;
    description: string;
}

export const ENGAGE_EMPTY_STATE: ErrorState = {
    message: 'No items available at this time',
    description: 'Please try refreshing the page. If the issue persists, contact your Panviva administrator.',
};

export const ENGAGE_UNAUTHORIZED_STATE: ErrorState = {
    message: 'Session expired',
    description: 'Your session has timed out. Please refresh the page and sign in again to continue.',
};

export const ENGAGE_FAILED_STATE: ErrorState = {
    message: 'Something went wrong',
    description: 'We’re having trouble connecting to Analytics right now. Please try refreshing the page. If the issue persists, contact your Panviva administrator.',
};

/**
 * Delay before redirecting an unauthorized user to the login flow.
 * Gives the user time to read the "Session expired" message first.
 */
export const UNAUTHORIZED_REDIRECT_DELAY_MS = 4000;

/**
 * Maps a thrown error from getDataGolToken / getEngageTile to a LoadStatus.
 * 401 → unauthorized, anything else → failed.
 */
export const getLoadStatusFromError = (error: unknown): LoadStatus => {
    const candidate = error as { response?: { status?: number } } | undefined;
    return candidate?.response?.status === 401 ? 'unauthorized' : 'failed';
};

/**
 * Fetch a short-lived DataGol embed token from the backend.
 * @param request The tenant code and dashboard the token is being minted for.
 * @returns Promise with the minted token payload.
 */
const getDataGolToken = (
    request: DataGolTokenRequest
): Promise<AxiosResponse<DataGolTokenResponse>> => {
    if (!request || !request.tenantCode || !request.dashboardId) {
        return Promise.reject(new Error('tenantCode and dashboardId are required.'));
    }

    return getHttpClient().post<DataGolTokenResponse>(`/embed/datagol/token`, request).catch((error: any) => {
        console.error(`Error fetching DataGol embed token:`, error);
        throw error;
    });
};

/**
 * Fetch the configured Engage dashboards.
 * @returns Promise with the list of available Engage dashboards.
 */
const getEngageTile = (): Promise<AxiosResponse<EngageTile[]>> => {
    return getHttpClient().get<EngageTile[]>(`/embed/datagol/dashboards`).catch((error: any) => {
        console.error(`Error fetching Engage dashboards:`, error);
        throw error;
    });
};

const engageService = {
    getDataGolToken,
    getEngageTile
};

export default engageService;
